import { API_CLIENT_ID_PATTERN, API_KEY_PREFIX, LEGACY_CLIENT_ID } from '@kkp-fraud/shared';
import type { RuleSetList } from './ruleSets';

/**
 * API Keys' pure half (Wave 4): the wire shapes of the admin service's `/api/api-clients`, what
 * the dialogs check before they send, and the ready-made curl example. The admin service decides
 * (`dotnet/admin-api`, `ApiClientEndpoints` / `ApiClientInput`); these mirror its rules so an
 * operator sees a problem before a round trip, not instead of one.
 *
 * The key itself exists in the browser only inside the one-time panel of the create / rotate
 * dialog, and is dropped when that dialog closes.
 */

export const API_CLIENT_ROUTES = {
  /** Admin only (`CanManageApiClients`). */
  clients: '/api/api-clients',
  /** API reference: `{ apiBase, tryPerMinute }`. Not an id (ids start with a letter). */
  reference: '/api/api-clients/_reference',
  /** Try it — one real `/v1/predetect` call with a pasted key. */
  try: '/api/api-clients/try',
} as const;

/** `GET /api/api-clients/_reference`. */
export interface ApiReferenceInfo {
  apiBase: string;
  tryPerMinute: number;
}

/** `POST /api/api-clients/try` — the key is sent once and kept nowhere. */
export interface ApiTryRequest {
  key: string;
  body: unknown;
  requestId?: string;
}

/** The decision API's own answer: its status (401 included), the round trip and its body. */
export interface ApiTryResult {
  status: number;
  latencyMs: number;
  replay: boolean;
  requestId?: string;
  body: unknown;
}

/** The key list's default: every key that is not revoked; `showRevoked` adds those back. */
export function visibleClients(clients: readonly ApiClientView[], showRevoked: boolean): ApiClientView[] {
  return showRevoked ? [...clients] : clients.filter((c) => c.status !== 'revoked');
}

export const apiClientPath = (id: string, suffix = ''): string => `${API_CLIENT_ROUTES.clients}/${encodeURIComponent(id)}${suffix}`;

/** `revoked` · `disabled` · `expired` · `active` — the admin service computes it; each is `apikeys.status.<code>`. */
export type ApiClientStatus = 'active' | 'disabled' | 'expired' | 'revoked';

/** `GET /api/api-clients` row — `ApiClientInput.View` in dotnet/admin-api. Never the key, never its hash. */
export interface ApiClientView {
  id: string;
  name: string;
  description?: string;
  /** The 8 characters after `kkpf_`. */
  keyPrefix: string;
  enabled: boolean;
  status: ApiClientStatus;
  expiresUtc?: string;
  revokedUtc?: string;
  rotatedUtc?: string;
  createdUtc: string;
  createdBy: string;
  updatedUtc: string;
  updatedBy: string;
  /** Sent back as `If-Match` on PATCH / rotate / revoke. */
  etag: string;
  /**
   * Wave 5 — the rule sets that judge this key's traffic; `[]` (or absent, from an older admin
   * service and the demo clients) = unbound: every enabled set judges.
   */
  ruleSets?: string[];
}

/** Create and rotate answer the client AND its key — the only time the key is ever sent. */
export interface ApiClientWithKey extends ApiClientView {
  key: string;
  /**
   * QA-A-15: the decision API a caller of this key calls — the admin service's
   * `DecisionApi:PublicBaseUrl`, else the API it talks to itself. Absent from an older service.
   */
  apiBase?: string;
}

export interface ApiClientCreateRequest {
  id: string;
  name: string;
  description?: string;
  enabled?: boolean;
  /** ISO 8601 with a zone. */
  expiresUtc?: string;
  /** Wave 5 — omit (or `[]`) for every enabled set. */
  ruleSets?: string[];
}

export interface ApiClientUpdateRequest {
  name?: string;
  /** `null` clears it. */
  description?: string | null;
  enabled?: boolean;
  /** `null` clears it. */
  expiresUtc?: string | null;
  /** Wave 5 — `[]` unbinds (every enabled set judges again). */
  ruleSets?: string[];
}

export const NAME_MAX = 100;
export const DESCRIPTION_MAX = 500;

/**
 * QA-A-15: what the curl example names when the admin service did not say which decision API
 * this console belongs to (an older service) — a shell variable to set, never a guessed address.
 */
export const API_BASE_VARIABLE = '$KKPF_API_BASE';

/** How a key is shown in a list: `kkpf_abcd1234…`. */
export const maskedKey = (keyPrefix: string): string => `${API_KEY_PREFIX}${keyPrefix}…`;

/** A problem the dialog can name before sending — the admin service's codes; each is `apikeys.problem.<code>`. */
export type ApiClientProblem = 'id_pattern' | 'id_reserved' | 'name_required' | 'name_too_long' | 'description_too_long' | 'expires_invalid' | 'expires_past';

export interface ApiClientForm {
  id: string;
  name: string;
  description: string;
  /** `yyyy-MM-dd` from a date input, or '' for no expiry. */
  expiresDate: string;
}

/**
 * A date picked in the dialog expires at the END of that day in Thai time (23:59:59 +07:00):
 * "valid until 31 Dec" reads as "through 31 Dec" to an operator in Bangkok.
 */
export function expiryInstant(date: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const iso = `${date}T23:59:59+07:00`;
  return Number.isNaN(Date.parse(iso)) ? null : iso;
}

/** The Thai calendar day (`yyyy-MM-dd`) an ISO instant falls on — the date input's value. */
export function expiryDate(iso: string | undefined): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  return new Date(ms + 7 * 3_600_000).toISOString().slice(0, 10);
}

export function apiClientFormProblems(form: ApiClientForm, checkId: boolean, now: number = Date.now()): ApiClientProblem[] {
  const out: ApiClientProblem[] = [];
  const id = form.id.trim();
  if (checkId) {
    if (!API_CLIENT_ID_PATTERN.test(id)) out.push('id_pattern');
    else if (id === LEGACY_CLIENT_ID) out.push('id_reserved');
  }
  const name = form.name.trim();
  if (name === '') out.push('name_required');
  else if (name.length > NAME_MAX) out.push('name_too_long');
  if (form.description.trim().length > DESCRIPTION_MAX) out.push('description_too_long');
  if (form.expiresDate !== '') {
    const at = expiryInstant(form.expiresDate);
    if (at === null) out.push('expires_invalid');
    else if (Date.parse(at) <= now) out.push('expires_past');
  }
  return out;
}

/** The field each problem is shown under. */
export const PROBLEM_FIELD: Record<ApiClientProblem, keyof ApiClientForm> = {
  id_pattern: 'id',
  id_reserved: 'id',
  name_required: 'name',
  name_too_long: 'name',
  description_too_long: 'description',
  expires_invalid: 'expiresDate',
  expires_past: 'expiresDate',
};

/**
 * The admin service's refusals an operator should read in words → i18n key. Anything else shows
 * the server's own message.
 */
const ERROR_KEYS: Record<string, string> = {
  api_client_exists: 'apikeys.error.exists',
  api_client_not_found: 'apikeys.error.not_found',
  api_client_revoked: 'apikeys.error.revoked',
  etag_mismatch: 'apikeys.error.etag_mismatch',
  if_match_required: 'apikeys.error.etag_mismatch',
  api_client_store_unavailable: 'apikeys.error.unavailable',
  rule_sets_unavailable: 'apikeys.error.rule_sets_unavailable',
};

export function apiClientErrorKey(code: string | undefined): string | null {
  return code ? (ERROR_KEYS[code] ?? null) : null;
}

/** Codes the admin service may send in `problems[].code` that the catalogue words (`apikeys.problem.<code>`). */
export const SERVER_PROBLEM_CODES: readonly string[] = [
  'id_required',
  'id_pattern',
  'id_reserved',
  'id_immutable',
  'name_required',
  'name_too_long',
  'description_type',
  'description_too_long',
  'enabled_type',
  'expires_invalid',
  'expires_past',
  'rule_sets_type',
  'rule_sets_too_many',
  'rule_set_unknown',
  'rule_set_archived',
];

/** One bound set as the Rule sets column shows it. */
export interface BoundSet {
  id: string;
  /** The registry's name; the id when the set is not in the list the console has. */
  name: string;
  /** Switched on and not archived — it judges this key's traffic. False while not known. */
  live: boolean;
  /** In the registry the console has (false: gone since, or the list is not known). */
  known: boolean;
}

export type Binding =
  | { unbound: true }
  | {
      unbound: false;
      sets: BoundSet[];
      /**
       * Every bound set is off (or archived, or unknown) — the engine judges with the primary
       * instead (`bindingFallback`). Only claimed when the registry is known.
       */
      fallsBack: boolean;
    };

/**
 * Wave 5 — what a key's binding means against the registry the console has. `list` null = not
 * known (a load in flight or failed): the ids are shown as they are, and no fallback is claimed.
 */
export function bindingOf(ruleSets: readonly string[] | undefined, list: RuleSetList | null): Binding {
  if (!ruleSets || ruleSets.length === 0) return { unbound: true };
  const sets: BoundSet[] = ruleSets.map((id) => {
    const s = list?.sets.find((x) => x.id === id);
    return { id, name: s?.name || id, live: Boolean(s && s.enabled && !s.archived), known: Boolean(s) };
  });
  return { unbound: false, sets, fallsBack: list !== null && sets.every((s) => !s.live) };
}

/** Tick or untick one set in a binding, keeping the order the sets were ticked in. */
export function toggleSet(ruleSets: readonly string[], id: string, on: boolean): string[] {
  const without = ruleSets.filter((x) => x !== id);
  return on ? [...without, id] : without;
}

/**
 * The ready-made example shown with a new key: the key goes into a shell variable (never into
 * the URL, and only once on the command line), then `/v1/predetect` with the repository's
 * sample request (`fixtures/sample-request.json`, run from the repository root).
 */
export function curlExample(key: string, base?: string): string {
  const api = base ? base.replace(/\/+$/, '') : API_BASE_VARIABLE;
  return [
    `export KKPF_KEY='${key}'`,
    '',
    `curl -sS -X POST ${api}/v1/predetect \\`,
    `  -H "Authorization: Bearer $KKPF_KEY" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  --data @fixtures/sample-request.json`,
  ].join('\n');
}
